import { CurrencyService } from "./currency.service";
import { Tradable } from "@models/interfaces/tradable";
import { InitService } from "@core-services/init.service";
import { Observable } from "rxjs";
import { AssetPrice } from "@models/tradable/split/asset_price";
import { DestructorService } from "@core-services/destructor.service";
import { TranslationService } from "@core-services/translation.service";
import { debug } from "@helpers/debug";
import { SocketService } from "@main-services/socket.service";

export abstract class TradableService<T extends Tradable> extends CurrencyService<T> {
	public abstract logoUrl(item: T, grayVersion: boolean): string;
	public abstract startSockets(): void;

	protected price$: Observable<T[]> = new Observable<T[]>();

	public constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		protected socketService: SocketService,
		protected translationService: TranslationService,
		name: string
	) {
		super(initService, destructor, translationService, name);
	}

	protected filteredList(): T[] {
		return this.list;
	}

	public getPrice(): Observable<T[]> {
		return this.price$;
	}

	public destroy(): void {
		super.destroy();
	}

	protected updateTradable(tradable: T, c: T): void {
		if (!tradable.getAssetPrices()) {
			tradable.setAssetPrices(new AssetPrice());
		}
		const prices: AssetPrice = tradable.getAssetPrices();
		// Price
		prices.setPrice(+c.getSocketAvgPrice());
		// Change in percent
		prices.setChange24h("" + c.getSocketChange24h());
		prices.setChange1w("" + c.getSocketChange1w());
		prices.setChange1m("" + c.getSocketChange1m());
		c?.getSocketChange6m() && prices.setChange6m("" + c.getSocketChange6m());
		prices.setChange1y("" + c.getSocketChange1y());
		c?.getSocketChange5y() && prices.setChange5y("" + c.getSocketChange5y());
		// Changed amount in euro
		prices.setChange24hAmount("" + c.getSocketChange24hAmount());
		prices.setChange1wAmount("" + c.getSocketChange1wAmount());
		prices.setChange1mAmount("" + c.getSocketChange1mAmount());
		c?.getSocketChange6mAmount() && prices.setChange6mAmount("" + c.getSocketChange6mAmount());
		prices.setChange1yAmount("" + c.getSocketChange1yAmount());
		c?.getSocketChange5yAmount() && prices.setChange5yAmount("" + c.getSocketChange5yAmount());
	}

	public getTradableToolTip(useCase: string, tradable: Tradable, isActionButton: boolean): string {
		if (tradable.isMaintenance() && (useCase === "send" || useCase === "deposit" || useCase === "")) {
			return "unavailable_under_maintenance";
		}
		if (!tradable.isAvailable() && (useCase === "buy" || useCase === "sell" || useCase === "")) {
			return "unavailable_under_maintenance";
		}

		if (!tradable.isBuyActive() && useCase === "buy") {
			return "action_buy_inactive_tooltip";
		}
		if (!tradable.isSellActive() && useCase === "sell") {
			return "action_sell_inactive_tooltip";
		}

		if (isActionButton) {
			return "action_" + useCase + "_tooltip";
		}
		return "";
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	protected initSockets(priceRoom: any, tradableRoom: any): void {
		this.price$ = this.socketService.joinCollectionRoom<T>(
			{
				name: priceRoom.room,
				type: priceRoom.type,
				isPrivate: priceRoom.isPrivate
			},
			priceRoom.collection
		);

		this.price$.subscribe((updated) => {
			if (this.list.length > 0) {
				if (debug) {
					console.log(`[SOCKET][${priceRoom.room}]: `, updated);
				}
				updated.forEach((a) => {
					const tradable = this.findBySymbol(a.getSymbol(true));
					if (!tradable) {
						// Not yet in list?
						return;
					}
					this.updateTradable(tradable, a);
				});
			}
			this._list.next(this.list);
		});

		this.socketService
			.joinCollectionRoom<T>(
				{
					name: tradableRoom.room,
					type: tradableRoom.type,
					isPrivate: tradableRoom.isPrivate
				},
				tradableRoom.collection
			)
			.subscribe((updated) => {
				if (this.list.length > 0) {
					updated.forEach((c) => {
						this.upsert(c);
					});
					this.list.sort((c1, c2) => c1.getSort() - c2.getSort());
				}
			});
	}
}
